import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  SafeAreaView,
  StatusBar,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  FlatList,
  Dimensions,
  Alert,
  Modal,
  Animated,
  PanResponder,
  ActivityIndicator,
  Linking,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  FontAwesome,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useVideoPlayer, VideoView } from 'expo-video';
import { setAudioModeAsync } from 'expo-audio';
import { BlurView } from 'expo-blur';
import Svg, { Path, Circle, Rect, Text as SvgText } from 'react-native-svg';

const { width, height } = Dimensions.get('window');

const LiveVideoSessionScreen = ({ route, navigation }) => {
  const { course } = route.params;
  const videoRef = useRef(null);
  const [isJoined, setIsJoined] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [participants, setParticipants] = useState(course?.enrolled || 156);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [questions, setQuestions] = useState([]);
  const [polls, setPolls] = useState([]);
  const [breakoutRooms, setBreakoutRooms] = useState([]);
  const [activeTab, setActiveTab] = useState('chat');
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [whiteboardVisible, setWhiteboardVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [reactions, setReactions] = useState([]);
  const [activePoll, setActivePoll] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState('good');
  const [bitrate, setBitrate] = useState('2.5 Mbps');
  const [latency, setLatency] = useState('45ms');
  const [showParticipants, setShowParticipants] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [videoQuality, setVideoQuality] = useState('HD');
  const [audioDevice, setAudioDevice] = useState('speaker');
  const [whiteboardPaths, setWhiteboardPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [whiteboardColor, setWhiteboardColor] = useState('#000000');
  const [whiteboardTool, setWhiteboardTool] = useState('pen');
  const [strokeWidth, setStrokeWidth] = useState(3);
  
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Sample data
  const slides = [
    { id: 1, title: 'Introduction', content: 'Welcome to Live Session' },
    { id: 2, title: 'Agenda', content: 'Session Topics & Timeline' },
    { id: 3, title: 'Key Concepts', content: 'Core Principles & Theory' },
    { id: 4, title: 'Live Demo', content: 'Practical Demonstration' },
    { id: 5, title: 'Q&A Session', content: 'Interactive Discussion' },
  ];

  const initialMessages = [
    { id: 1, user: 'Instructor', message: 'Welcome everyone! We\'ll start in 2 minutes.', time: '09:58', isInstructor: true, avatar: '👨‍🏫' },
    { id: 2, user: 'Ahmed', message: 'Excited for this workshop!', time: '09:59', isInstructor: false, avatar: '👤' },
    { id: 3, user: 'Sarah', message: 'Is recording available after?', time: '10:00', isInstructor: false, avatar: '👩‍💼' },
    { id: 4, user: 'Instructor', message: 'Yes, recording will be shared within 24 hours.', time: '10:01', isInstructor: true, avatar: '👨‍🏫' },
    { id: 5, user: 'David', message: 'Can we ask questions during demo?', time: '10:02', isInstructor: false, avatar: '👨‍💻' },
  ];

  const sampleParticipants = [
    { id: 1, name: 'Dr. Ahmed Hassan', role: 'Host', isSpeaking: true, avatar: '👨‍🏫' },
    { id: 2, name: 'Sarah Johnson', role: 'Co-host', isSpeaking: false, avatar: '👩‍💼' },
    { id: 3, name: 'You', role: 'Participant', isSpeaking: false, avatar: '👤' },
    { id: 4, name: 'Mohammed Ali', role: 'Participant', isSpeaking: false, avatar: '👨‍💻' },
    { id: 5, name: 'Lisa Wang', role: 'Participant', isSpeaking: false, avatar: '👩‍🔬' },
    { id: 6, name: 'Alex Rodriguez', role: 'Participant', isSpeaking: false, avatar: '👨‍🔧' },
  ];

  const whiteboardColors = [
    '#000000', '#ef4444', '#f59e0b', '#10b981', '#0ea5e9', '#8b5cf6', '#ec4899'
  ];

  const whiteboardTools = [
    { id: 'pen', icon: 'pen', name: 'Pen' },
    { id: 'line', icon: 'vector-line', name: 'Line' },
    { id: 'rectangle', icon: 'rectangle-outline', name: 'Rectangle' },
    { id: 'circle', icon: 'circle-outline', name: 'Circle' },
    { id: 'text', icon: 'format-text', name: 'Text' },
    { id: 'eraser', icon: 'eraser', name: 'Eraser' },
  ];

  useEffect(() => {
    setMessages(initialMessages);
    startAnimations();
    preventScreenCapture();
    
    // Simulate live updates
    const interval = setInterval(() => {
      setParticipants(prev => prev + Math.floor(Math.random() * 3));
      updateConnectionMetrics();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const preventScreenCapture = async () => {
    await ScreenCapture.preventScreenCaptureAsync();
  };

  const updateConnectionMetrics = () => {
    const qualities = ['poor', 'fair', 'good', 'excellent'];
    const bitrates = ['1.2 Mbps', '1.8 Mbps', '2.5 Mbps', '3.2 Mbps'];
    const latencies = ['120ms', '80ms', '45ms', '25ms'];
    
    const random = Math.floor(Math.random() * 4);
    setConnectionQuality(qualities[random]);
    setBitrate(bitrates[random]);
    setLatency(latencies[random]);
  };

  // ── Real video playback ─────────────────────────────────────────────────────
  // expo-video plays direct media (HLS .m3u8 or .mp4). Meeting links (Zoom, Meet,
  // Teams, Webex) can't be embedded in a native player, so those open externally.
  // Point course.streamUrl (or hlsUrl) at a real stream from your backend and it
  // will play live in-app.
  //
  // DEMO_STREAM is a real public HLS test stream used ONLY as a fallback so the
  // player is visibly functional during development. Replace it / remove the
  // fallback once your backend provides real stream_url values.
  const DEMO_STREAM = 'https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8';

  const isPlayableMedia = (u) => typeof u === 'string' && /\.(m3u8|mp4|mov|m4v|webm)(\?|#|$)/i.test(u);
  const isMeetingLink   = (u) => typeof u === 'string' && /(zoom\.us|meet\.google|teams\.microsoft|webex\.com)/i.test(u);

  const realStreamUrl = useMemo(() => {
    const candidates = [course?.streamUrl, course?.hlsUrl, course?.videoUrl, course?.recordingUrl];
    return candidates.find(isPlayableMedia) || null;
  }, [course?.streamUrl, course?.hlsUrl, course?.videoUrl, course?.recordingUrl]);

  const usingDemo  = !realStreamUrl;
  const streamUrl  = realStreamUrl || DEMO_STREAM;     // always a playable source
  const meetingUrl = isMeetingLink(course?.videoUrl) ? course.videoUrl : null;

  // Create the player. Unmuted so the viewer hears the instructor — this is the
  // incoming stream's audio and is independent of the participant's mic-mute.
  const player = useVideoPlayer(streamUrl, (p) => {
    if (!p) return;
    p.loop = false;
    p.muted = false;
  });

  // Play when the user has joined; pause when they leave.
  useEffect(() => {
    if (!player) return;
    try { isJoined ? player.play() : player.pause(); } catch {}
  }, [isJoined, streamUrl, player]);

  const handleJoinSession = async () => {
    try {
      setLoading(true);
      // Allow video audio in silent mode, but NEVER play in the background
      // (background audio caused a prior App Store rejection). Non-blocking.
      await setAudioModeAsync({
        allowsRecording: false,
        playsInSilentMode: true,
        shouldPlayInBackground: false,
        interruptionMode: 'mixWithOthers',
        interruptionModeAndroid: 'duckOthers',
        shouldRouteThroughEarpiece: false,
      }).catch(() => {});
      
      // Simulate joining process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setIsJoined(true);
      Alert.alert('Joined Successfully', 'You are now in the live session');
      
      // Schedule auto-leave reminder
      setTimeout(() => {
        Alert.alert('Session Reminder', 'Session ends in 15 minutes');
      }, 45 * 60 * 1000); // 45 minutes
      
    } catch (error) {
      Alert.alert('Connection Error', 'Could not join session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveSession = () => {
    Alert.alert(
      'Leave Session',
      'Are you sure you want to leave the session?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Leave', 
          style: 'destructive',
          onPress: () => {
            setIsJoined(false);
            navigation.goBack();
          }
        }
      ]
    );
  };

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      const message = {
        id: Date.now(),
        user: 'You',
        message: newMessage,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isInstructor: false,
        avatar: '👤'
      };
      setMessages(prev => [...prev, message]);
      setNewMessage('');
    }
  };

  const handleAskQuestion = () => {
    Alert.prompt(
      'Ask a Question',
      'Type your question for the instructor:',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Ask',
          onPress: (question) => {
            if (question) {
              const newQuestion = {
                id: Date.now(),
                question,
                user: 'You',
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                likes: 0,
                answered: false,
                avatar: '❓'
              };
              setQuestions(prev => [...prev, newQuestion]);
              Alert.alert('Question Submitted', 'Your question has been sent to the instructor');
            }
          }
        }
      ]
    );
  };

  const handleCreatePoll = () => {
    Alert.prompt(
      'Create Quick Poll',
      'Enter poll question:',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Next',
          onPress: (question) => {
            if (question) {
              Alert.prompt(
                'Poll Options',
                'Enter options (comma separated):',
                [
                  { text: 'Cancel', style: 'cancel' },
                  {
                    text: 'Create',
                    onPress: (options) => {
                      if (options) {
                        const optionsArray = options.split(',').map((opt, idx) => ({
                          id: idx + 1,
                          text: opt.trim(),
                          votes: 0,
                          percentage: 0
                        }));
                        const newPoll = {
                          id: Date.now(),
                          question,
                          options: optionsArray,
                          createdBy: 'You',
                          totalVotes: 0,
                          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        };
                        setPolls(prev => [...prev, newPoll]);
                        setActivePoll(newPoll);
                      }
                    }
                  }
                ]
              );
            }
          }
        }
      ]
    );
  };

  const handleVotePoll = (pollId, optionId) => {
    setPolls(prev => prev.map(poll => {
      if (poll.id === pollId) {
        const updatedOptions = poll.options.map(option => {
          if (option.id === optionId) {
            return { ...option, votes: option.votes + 1 };
          }
          return option;
        });
        const totalVotes = updatedOptions.reduce((sum, opt) => sum + opt.votes, 0);
        const optionsWithPercentages = updatedOptions.map(opt => ({
          ...opt,
          percentage: totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0
        }));
        return { ...poll, options: optionsWithPercentages, totalVotes };
      }
      return poll;
    }));
  };

  const handleRaiseHand = () => {
    setIsHandRaised(!isHandRaised);
    Alert.alert(isHandRaised ? 'Hand Lowered' : 'Hand Raised', 
      isHandRaised ? 'Hand lowered' : 'The instructor has been notified');
  };

  const handleScreenShare = async () => {
    try {
      if (!isScreenSharing) {
        const { status } = await ScreenCapture.requestPermissionsAsync();
        if (status === 'granted') {
          setIsScreenSharing(true);
          Alert.alert('Screen Sharing', 'You are now sharing your screen');
        }
      } else {
        setIsScreenSharing(false);
        Alert.alert('Screen Sharing', 'Screen sharing stopped');
      }
    } catch (error) {
      Alert.alert('Permission Required', 'Screen sharing permission is required');
    }
  };

  const handleReaction = (reaction) => {
    const newReaction = {
      id: Date.now(),
      reaction,
      user: 'You',
      time: new Date().getTime()
    };
    setReactions(prev => [...prev, newReaction]);
    
    // Remove reaction after 3 seconds
    setTimeout(() => {
      setReactions(prev => prev.filter(r => r.id !== newReaction.id));
    }, 3000);
  };

  const handleRecording = () => {
    setIsRecording(!isRecording);
    Alert.alert(
      isRecording ? 'Recording Stopped' : 'Recording Started',
      isRecording ? 'Session recording stopped' : 'Session is being recorded locally'
    );
  };

  const handleBreakoutRooms = () => {
    Alert.alert(
      'Breakout Rooms',
      'Create breakout rooms for group discussions?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Create 4 Rooms',
          onPress: () => {
            const rooms = Array.from({ length: 4 }, (_, i) => ({
              id: i + 1,
              name: `Room ${i + 1}`,
              topic: ['Case Study Discussion', 'Problem Solving', 'Group Project', 'Brainstorming'][i],
              participants: Math.floor(Math.random() * 8) + 3,
              host: sampleParticipants[i + 2]?.name || 'Moderator'
            }));
            setBreakoutRooms(rooms);
            Alert.alert('Breakout Rooms Created', '4 rooms have been created. You can assign participants.');
          }
        }
      ]
    );
  };

  const handleWhiteboard = () => {
    setWhiteboardVisible(!whiteboardVisible);
  };

  const handleWhiteboardDraw = (event) => {
    if (whiteboardTool === 'pen') {
      const { locationX, locationY } = event.nativeEvent;
      const newPoint = { x: locationX, y: locationY, color: whiteboardColor, width: strokeWidth };
      
      setCurrentPath(prev => {
        const newPath = [...prev, newPoint];
        if (newPath.length > 1) {
          setWhiteboardPaths(prevPaths => [
            ...prevPaths,
            { id: Date.now(), points: [...newPath], color: whiteboardColor, width: strokeWidth }
          ]);
        }
        return newPath;
      });
    }
  };

  const handleClearWhiteboard = () => {
    Alert.alert(
      'Clear Whiteboard',
      'Clear all drawings?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Clear',
          style: 'destructive',
          onPress: () => {
            setWhiteboardPaths([]);
            setCurrentPath([]);
          }
        }
      ]
    );
  };

  const renderVideoSection = () => (
    <Animated.View 
      style={[
        styles.videoSection,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={['#000000', '#1a1a1a']}
        style={styles.videoContainer}
      >
        {/* Session Header */}
        <View style={styles.sessionHeader}>
          <View style={styles.sessionInfo}>
            <View style={styles.liveStatus}>
              <View style={styles.liveDot} />
              <Text style={styles.liveText}>LIVE</Text>
            </View>
            <Text style={styles.sessionTitle} numberOfLines={1}>{course?.title}</Text>
          </View>
          
          <View style={styles.sessionStats}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="account-group" size={14} color="#ffffff" />
              <Text style={styles.statText}>{participants}</Text>
            </View>
            <View style={[styles.connectionBadge, styles[`${connectionQuality}Connection`]]}>
              <MaterialCommunityIcons name="wifi" size={12} color="#ffffff" />
              <Text style={styles.connectionText}>{connectionQuality.toUpperCase()}</Text>
            </View>
          </View>
        </View>

        {/* Main Video Area */}
        <View style={styles.videoArea}>
          {!isJoined ? (
            <View style={styles.joinPrompt}>
              <MaterialCommunityIcons name="video" size={64} color="#ffffff" />
              <Text style={styles.joinTitle}>Ready to Join</Text>
              <Text style={styles.joinSubtitle}>{course?.title}</Text>
              
              {loading ? (
                <ActivityIndicator size={36} color="#ffffff" style={styles.loadingIndicator} />
              ) : (
                <TouchableOpacity style={styles.joinButton} onPress={handleJoinSession}>
                  <MaterialCommunityIcons name="video-plus" size={24} color="#ffffff" />
                  <Text style={styles.joinButtonText}>JOIN SESSION</Text>
                </TouchableOpacity>
              )}
              
              <Text style={styles.joinDetails}>
                Duration: {course?.duration} • {course?.enrolled} participants
              </Text>
            </View>
          ) : (
            <View style={styles.liveVideo}>
              {/* Instructor Video */}
              <View style={styles.instructorVideo}>
                <VideoView
                  player={player}
                  style={styles.realVideo}
                  contentFit="cover"
                  nativeControls
                  allowsFullscreen
                  allowsPictureInPicture={false}
                />
                {usingDemo && (
                  <View style={styles.demoTag}>
                    <Text style={styles.demoTagText}>DEMO STREAM</Text>
                  </View>
                )}
                {meetingUrl && (
                  <TouchableOpacity
                    style={styles.openMeetingBtn}
                    onPress={() => Linking.openURL(meetingUrl).catch(() => Alert.alert('Unable to open', 'Could not open the meeting link.'))}
                  >
                    <MaterialCommunityIcons name="open-in-new" size={16} color="#ffffff" />
                    <Text style={styles.openMeetingText}>Open meeting room</Text>
                  </TouchableOpacity>
                )}
                {isSpeaking('Dr. Ahmed Hassan') && (
                  <View style={styles.speakingIndicator}>
                    <MaterialCommunityIcons name="microphone" size={16} color="#ffffff" />
                  </View>
                )}
              </View>

              {/* Presentation Slides */}
              <View style={styles.slidesContainer}>
                <Text style={styles.slidesTitle}>📊 Presentation</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {slides.map((slide, index) => (
                    <TouchableOpacity
                      key={slide.id}
                      style={[
                        styles.slideCard,
                        currentSlide === index && styles.activeSlide
                      ]}
                      onPress={() => setCurrentSlide(index)}
                    >
                      <View style={styles.slideNumber}>
                        <Text style={styles.slideNumberText}>{index + 1}</Text>
                      </View>
                      <Text style={styles.slideTitle}>{slide.title}</Text>
                      <Text style={styles.slideContent}>{slide.content}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>

              {/* Reactions */}
              {reactions.length > 0 && (
                <View style={styles.reactionsContainer}>
                  {reactions.map(reaction => (
                    <Animated.View key={reaction.id} style={styles.reactionBubble}>
                      <Text style={styles.reactionText}>{reaction.reaction}</Text>
                    </Animated.View>
                  ))}
                </View>
              )}
            </View>
          )}
        </View>

        {/* Active Poll Banner */}
        {activePoll && (
          <View style={styles.pollBanner}>
            <View style={styles.pollBannerContent}>
              <MaterialCommunityIcons name="poll" size={20} color="#ffffff" />
              <Text style={styles.pollBannerText} numberOfLines={1}>
                Live Poll: {activePoll.question}
              </Text>
            </View>
            <TouchableOpacity onPress={() => setActivePoll(null)}>
              <MaterialCommunityIcons name="close" size={20} color="#ffffff" />
            </TouchableOpacity>
          </View>
        )}
      </LinearGradient>
    </Animated.View>
  );

  const renderControls = () => (
    <View style={styles.controlsSection}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.controlsScroll}>
        <TouchableOpacity 
          style={[styles.controlButton, isMuted && styles.controlButtonActive]}
          onPress={() => setIsMuted(!isMuted)}
        >
          <MaterialCommunityIcons 
            name={isMuted ? "microphone-off" : "microphone"} 
            size={24} 
            color={isMuted ? "#ef4444" : "#ffffff"} 
          />
          <Text style={styles.controlText}>{isMuted ? 'Unmute' : 'Mute'}</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[styles.controlButton, isVideoOn && styles.controlButtonActive]}
          onPress={() => setIsVideoOn(!isVideoOn)}
        >
          <MaterialCommunityIcons 
            name={isVideoOn ? "video-off" : "video"} 
            size={24} 
            color="#ffffff" 
          />
          <Text style={styles.controlText}>{isVideoOn ? 'Stop Video' : 'Start Video'}</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[styles.controlButton, isScreenSharing && styles.controlButtonActive]}
          onPress={handleScreenShare}
        >
          <MaterialCommunityIcons 
            name={isScreenSharing ? "monitor-share" : "monitor"} 
            size={24} 
            color="#ffffff" 
          />
          <Text style={styles.controlText}>Share Screen</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[styles.controlButton, isHandRaised && styles.controlButtonActive]}
          onPress={handleRaiseHand}
        >
          <MaterialCommunityIcons 
            name={isHandRaised ? "hand-back-left" : "hand-back-left-outline"}
            size={24} 
            color={isHandRaised ? "#f59e0b" : "#ffffff"} 
          />
          <Text style={styles.controlText}>Raise Hand</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={styles.controlButton}
          onPress={handleAskQuestion}
        >
          <MaterialCommunityIcons name="help-circle" size={24} color="#ffffff" />
          <Text style={styles.controlText}>Ask Question</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={styles.controlButton}
          onPress={handleCreatePoll}
        >
          <MaterialCommunityIcons name="poll" size={24} color="#ffffff" />
          <Text style={styles.controlText}>Create Poll</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[styles.controlButton, whiteboardVisible && styles.controlButtonActive]}
          onPress={handleWhiteboard}
        >
          <MaterialCommunityIcons name="draw" size={24} color="#ffffff" />
          <Text style={styles.controlText}>Whiteboard</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[styles.controlButton, breakoutRooms.length > 0 && styles.controlButtonActive]}
          onPress={handleBreakoutRooms}
        >
          <MaterialCommunityIcons name="google-classroom" size={24} color="#ffffff" />
          <Text style={styles.controlText}>Breakout</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[styles.controlButton, isRecording && styles.controlButtonActive]}
          onPress={handleRecording}
        >
          <MaterialCommunityIcons 
            name={isRecording ? "record-circle" : "record-circle-outline"} 
            size={24} 
            color={isRecording ? "#ef4444" : "#ffffff"} 
          />
          <Text style={styles.controlText}>Record</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={[styles.controlButton]}
          onPress={() => setShowSettings(true)}
        >
          <MaterialCommunityIcons name="cog" size={24} color="#ffffff" />
          <Text style={styles.controlText}>Settings</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );

  const renderChatSection = () => (
    <View style={styles.chatSection}>
      <View style={styles.chatHeader}>
        <Text style={styles.chatTitle}>💬 Live Chat</Text>
        <View style={styles.chatActions}>
          <TouchableOpacity onPress={() => setMessages([])}>
            <MaterialCommunityIcons name="delete" size={20} color="#64748B" />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setShowParticipants(true)}>
            <MaterialCommunityIcons name="account-group" size={20} color="#64748B" />
          </TouchableOpacity>
        </View>
      </View>
      
      <FlatList
        data={messages}
        keyExtractor={(item) => item.id.toString()}
        style={styles.chatMessages}
        renderItem={({ item }) => (
          <View style={[
            styles.messageContainer,
            item.isInstructor && styles.instructorMessage
          ]}>
            <View style={styles.messageHeader}>
              <Text style={styles.messageAvatar}>{item.avatar}</Text>
              <View style={styles.messageInfo}>
                <Text style={[
                  styles.messageUser,
                  item.isInstructor && styles.instructorName
                ]}>
                  {item.user}
                </Text>
                <Text style={styles.messageTime}>{item.time}</Text>
              </View>
            </View>
            <Text style={styles.messageText}>{item.message}</Text>
          </View>
        )}
      />

      <View style={styles.chatInputContainer}>
        <View style={styles.reactionButtons}>
          {['👍', '👏', '🎉', '❓', '🔥'].map((reaction, index) => (
            <TouchableOpacity 
              key={index}
              style={styles.reactionButton}
              onPress={() => handleReaction(reaction)}
            >
              <Text style={styles.reactionEmoji}>{reaction}</Text>
            </TouchableOpacity>
          ))}
        </View>
        
        <View style={styles.inputRow}>
          <TextInput
            style={styles.chatInput}
            placeholder="Type your message..."
            value={newMessage}
            onChangeText={setNewMessage}
            multiline
            placeholderTextColor="#6b7280"
          />
          <TouchableOpacity 
            style={[styles.sendButton, !newMessage.trim() && styles.sendButtonDisabled]}
            onPress={handleSendMessage}
            disabled={!newMessage.trim()}
          >
            <MaterialCommunityIcons 
              name="send" 
              size={20} 
              color={newMessage.trim() ? "#ffffff" : "#9ca3af"} 
            />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderQuestionsSection = () => (
    <View style={styles.questionsSection}>
      <View style={styles.questionsHeader}>
        <Text style={styles.questionsTitle}>❓ Questions ({questions.length})</Text>
        <TouchableOpacity onPress={handleAskQuestion}>
          <MaterialCommunityIcons name="plus-circle" size={20} color="#0e4e96" />
        </TouchableOpacity>
      </View>
      
      <FlatList
        data={questions}
        keyExtractor={(item) => item.id.toString()}
        style={styles.questionsList}
        renderItem={({ item }) => (
          <View style={styles.questionCard}>
            <View style={styles.questionHeader}>
              <Text style={styles.questionAvatar}>{item.avatar}</Text>
              <View style={styles.questionInfo}>
                <Text style={styles.questionUser}>{item.user}</Text>
                <Text style={styles.questionTime}>{item.time}</Text>
              </View>
            </View>
            <Text style={styles.questionText}>{item.question}</Text>
            <View style={styles.questionActions}>
              <TouchableOpacity style={styles.likeButton}>
                <MaterialCommunityIcons name="thumb-up" size={16} color="#64748B" />
                <Text style={styles.likeCount}>{item.likes}</Text>
              </TouchableOpacity>
              <View style={[
                styles.answerStatus,
                item.answered && styles.answeredStatus
              ]}>
                <Text style={styles.answerStatusText}>
                  {item.answered ? '✓ Answered' : 'Pending'}
                </Text>
              </View>
            </View>
          </View>
        )}
      />
    </View>
  );

  const renderPollsSection = () => (
    <View style={styles.pollsSection}>
      <View style={styles.pollsHeader}>
        <Text style={styles.pollsTitle}>📊 Live Polls ({polls.length})</Text>
        <TouchableOpacity onPress={handleCreatePoll}>
          <MaterialCommunityIcons name="plus-circle" size={20} color="#0e4e96" />
        </TouchableOpacity>
      </View>
      
      {activePoll && (
        <View style={styles.activePollCard}>
          <View style={styles.pollHeader}>
            <Text style={styles.pollQuestion}>{activePoll.question}</Text>
            <Text style={styles.pollTime}>{activePoll.time}</Text>
          </View>
          
          {activePoll.options.map((option) => (
            <TouchableOpacity
              key={option.id}
              style={styles.pollOption}
              onPress={() => handleVotePoll(activePoll.id, option.id)}
            >
              <View style={styles.optionRow}>
                <View style={styles.optionCircle} />
                <Text style={styles.optionText}>{option.text}</Text>
              </View>
              <View style={styles.pollResult}>
                <View style={styles.pollBarContainer}>
                  <View 
                    style={[
                      styles.pollBar, 
                      { width: `${option.percentage}%` }
                    ]} 
                  />
                </View>
                <Text style={styles.optionPercentage}>{option.percentage}%</Text>
              </View>
            </TouchableOpacity>
          ))}
          
          <Text style={styles.totalVotes}>{activePoll.totalVotes} total votes</Text>
        </View>
      )}
    </View>
  );

  const renderWhiteboard = () => (
    <Modal
      visible={whiteboardVisible}
      animationType="slide"
      presentationStyle="fullScreen"
    >
      <SafeAreaView style={styles.whiteboardContainer}>
        <View style={styles.whiteboardHeader}>
          <TouchableOpacity onPress={() => setWhiteboardVisible(false)}>
            <Ionicons name="arrow-back" size={24} color="#0e4e96" />
          </TouchableOpacity>
          <Text style={styles.whiteboardTitle}>🎨 Collaborative Whiteboard</Text>
          <View style={styles.whiteboardActions}>
            <TouchableOpacity onPress={handleClearWhiteboard}>
              <MaterialCommunityIcons name="trash-can" size={20} color="#ef4444" />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => Alert.alert('Saved', 'Whiteboard saved to notes')}>
              <MaterialCommunityIcons name="content-save" size={20} color="#0e4e96" />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.whiteboardToolbar}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {whiteboardTools.map((tool) => (
              <TouchableOpacity
                key={tool.id}
                style={[
                  styles.whiteboardToolButton,
                  whiteboardTool === tool.id && styles.activeWhiteboardTool
                ]}
                onPress={() => setWhiteboardTool(tool.id)}
              >
                <MaterialCommunityIcons 
                  name={tool.icon} 
                  size={24} 
                  color={whiteboardTool === tool.id ? '#ffffff' : '#0e4e96'} 
                />
                <Text style={styles.whiteboardToolText}>{tool.name}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        <View style={styles.colorPicker}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {whiteboardColors.map((color) => (
              <TouchableOpacity
                key={color}
                style={[
                  styles.colorButton,
                  { backgroundColor: color },
                  whiteboardColor === color && styles.activeColorButton
                ]}
                onPress={() => setWhiteboardColor(color)}
              />
            ))}
          </ScrollView>
          
          <View style={styles.strokePicker}>
            {[1, 3, 5, 8].map((width) => (
              <TouchableOpacity
                key={width}
                style={[
                  styles.strokeButton,
                  strokeWidth === width && styles.activeStrokeButton
                ]}
                onPress={() => setStrokeWidth(width)}
              >
                <View style={[
                  styles.strokePreview,
                  { width: width * 3, height: width * 3, backgroundColor: whiteboardColor }
                ]} />
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View 
          style={styles.whiteboardArea}
          onTouchStart={handleWhiteboardDraw}
          onTouchMove={handleWhiteboardDraw}
        >
          <Svg style={styles.whiteboardSvg}>
            {whiteboardPaths.map((path) => {
              if (path.points.length > 1) {
                const pathData = path.points.map((point, index) => 
                  `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
                ).join(' ');
                return (
                  <Path
                    key={path.id}
                    d={pathData}
                    stroke={path.color}
                    strokeWidth={path.width}
                    fill="none"
                  />
                );
              }
              return null;
            })}
          </Svg>
        </View>
      </SafeAreaView>
    </Modal>
  );

  const renderParticipantsModal = () => (
    <Modal
      visible={showParticipants}
      animationType="slide"
      transparent
    >
      <View style={styles.modalOverlay}>
        <View style={styles.participantsModal}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>👥 Participants ({sampleParticipants.length})</Text>
            <TouchableOpacity onPress={() => setShowParticipants(false)}>
              <Ionicons name="close" size={24} color="#0e4e96" />
            </TouchableOpacity>
          </View>
          
          <FlatList
            data={sampleParticipants}
            keyExtractor={(item) => item.id.toString()}
            renderItem={({ item }) => (
              <View style={styles.participantRow}>
                <View style={styles.participantInfo}>
                  <Text style={styles.participantAvatar}>{item.avatar}</Text>
                  <View>
                    <Text style={styles.participantName}>{item.name}</Text>
                    <Text style={styles.participantRole}>{item.role}</Text>
                  </View>
                </View>
                {item.isSpeaking && (
                  <View style={styles.speakingBadge}>
                    <MaterialCommunityIcons name="microphone" size={16} color="#ffffff" />
                  </View>
                )}
              </View>
            )}
          />
          
          <View style={styles.modalFooter}>
            <TouchableOpacity style={styles.muteAllButton}>
              <MaterialCommunityIcons name="microphone-off" size={20} color="#ffffff" />
              <Text style={styles.muteAllText}>Mute All</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderSettingsModal = () => (
    <Modal
      visible={showSettings}
      animationType="slide"
      transparent
    >
      <BlurView intensity={90} style={styles.settingsOverlay}>
        <View style={styles.settingsModal}>
          <View style={styles.settingsHeader}>
            <Text style={styles.settingsTitle}>⚙️ Settings</Text>
            <TouchableOpacity onPress={() => setShowSettings(false)}>
              <Ionicons name="close" size={24} color="#0e4e96" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.settingsContent}>
            <View style={styles.settingsSection}>
              <Text style={styles.settingsSectionTitle}>Video Quality</Text>
              {['Low', 'Medium', 'HD', 'Full HD'].map((quality) => (
                <TouchableOpacity
                  key={quality}
                  style={[
                    styles.settingsOption,
                    videoQuality === quality && styles.selectedOption
                  ]}
                  onPress={() => setVideoQuality(quality)}
                >
                  <Text style={styles.optionText}>{quality}</Text>
                  {videoQuality === quality && (
                    <MaterialCommunityIcons name="check" size={20} color="#0e4e96" />
                  )}
                </TouchableOpacity>
              ))}
            </View>
            
            <View style={styles.settingsSection}>
              <Text style={styles.settingsSectionTitle}>Audio Device</Text>
              {['Speaker', 'Earpiece', 'Bluetooth', 'Headphones'].map((device) => (
                <TouchableOpacity
                  key={device}
                  style={[
                    styles.settingsOption,
                    audioDevice === device && styles.selectedOption
                  ]}
                  onPress={() => setAudioDevice(device)}
                >
                  <Text style={styles.optionText}>{device}</Text>
                  {audioDevice === device && (
                    <MaterialCommunityIcons name="check" size={20} color="#0e4e96" />
                  )}
                </TouchableOpacity>
              ))}
            </View>
            
            <View style={styles.settingsSection}>
              <Text style={styles.settingsSectionTitle}>Connection Info</Text>
              <View style={styles.connectionInfo}>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Status:</Text>
                  <View style={[styles.connectionStatus, styles[`${connectionQuality}Status`]]}>
                    <Text style={styles.infoValue}>{connectionQuality.toUpperCase()}</Text>
                  </View>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Bitrate:</Text>
                  <Text style={styles.infoValue}>{bitrate}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Latency:</Text>
                  <Text style={styles.infoValue}>{latency}</Text>
                </View>
              </View>
            </View>
          </ScrollView>
          
          <View style={styles.settingsFooter}>
            <TouchableOpacity style={styles.testAudioButton}>
              <Text style={styles.testAudioText}>Test Audio</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.testVideoButton}>
              <Text style={styles.testVideoText}>Test Video</Text>
            </TouchableOpacity>
          </View>
        </View>
      </BlurView>
    </Modal>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'chat':
        return renderChatSection();
      case 'questions':
        return renderQuestionsSection();
      case 'polls':
        return renderPollsSection();
      default:
        return renderChatSection();
    }
  };

  const isSpeaking = (name) => {
    return sampleParticipants.find(p => p.name === name)?.isSpeaking || false;
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#000" />
      
      {renderVideoSection()}
      {renderControls()}
      
      <View style={styles.tabsContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'chat' && styles.activeTab]}
            onPress={() => setActiveTab('chat')}
          >
            <MaterialCommunityIcons 
              name="chat" 
              size={20} 
              color={activeTab === 'chat' ? '#0e4e96' : '#6b7280'} 
            />
            <Text style={[styles.tabText, activeTab === 'chat' && styles.activeTabText]}>
              Chat
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.tab, activeTab === 'questions' && styles.activeTab]}
            onPress={() => setActiveTab('questions')}
          >
            <MaterialCommunityIcons 
              name="help-circle" 
              size={20} 
              color={activeTab === 'questions' ? '#0e4e96' : '#6b7280'} 
            />
            <Text style={[styles.tabText, activeTab === 'questions' && styles.activeTabText]}>
              Questions ({questions.length})
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.tab, activeTab === 'polls' && styles.activeTab]}
            onPress={() => setActiveTab('polls')}
          >
            <MaterialCommunityIcons 
              name="poll" 
              size={20} 
              color={activeTab === 'polls' ? '#0e4e96' : '#6b7280'} 
            />
            <Text style={[styles.tabText, activeTab === 'polls' && styles.activeTabText]}>
              Polls ({polls.length})
            </Text>
          </TouchableOpacity>
          
          {breakoutRooms.length > 0 && (
            <TouchableOpacity
              style={[styles.tab]}
              onPress={() => navigation.navigate('BreakoutRooms', { rooms: breakoutRooms })}
            >
              <MaterialCommunityIcons name="google-classroom" size={20} color="#6b7280" />
              <Text style={styles.tabText}>
                Breakout ({breakoutRooms.length})
              </Text>
            </TouchableOpacity>
          )}
        </ScrollView>
      </View>

      {renderTabContent()}
      
      {/* Leave Button */}
      <TouchableOpacity 
        style={styles.leaveButton}
        onPress={handleLeaveSession}
      >
        <MaterialCommunityIcons name="phone-hangup" size={24} color="#ffffff" />
        <Text style={styles.leaveButtonText}>Leave Session</Text>
      </TouchableOpacity>

      {renderWhiteboard()}
      {renderParticipantsModal()}
      {renderSettingsModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#000',
  },
  videoSection: {
    backgroundColor: '#000',
  },
  videoContainer: {
    padding: 16,
  },
  sessionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  sessionInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  liveStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ef4444',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 12,
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#ffffff',
    marginRight: 6,
  },
  liveText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  sessionTitle: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
  },
  sessionStats: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#333',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    marginRight: 10,
  },
  statText: {
    color: '#ffffff',
    fontSize: 12,
    marginLeft: 4,
  },
  connectionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  goodConnection: {
    backgroundColor: '#10b981',
  },
  excellentConnection: {
    backgroundColor: '#0ea5e9',
  },
  fairConnection: {
    backgroundColor: '#f59e0b',
  },
  poorConnection: {
    backgroundColor: '#ef4444',
  },
  connectionText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
    marginLeft: 4,
  },
  videoArea: {
    minHeight: 300,
    justifyContent: 'center',
  },
  joinPrompt: {
    alignItems: 'center',
    padding: 24,
  },
  joinTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginTop: 20,
    marginBottom: 8,
  },
  joinSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.8,
    marginBottom: 32,
    textAlign: 'center',
  },
  loadingIndicator: {
    marginTop: 20,
  },
  joinButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0e4e96',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 12,
    marginBottom: 20,
  },
  joinButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 'bold',
    marginLeft: 12,
  },
  joinDetails: {
    color: '#ffffff',
    fontSize: 14,
    opacity: 0.7,
  },
  liveVideo: {
    flex: 1,
  },
  instructorVideo: {
    alignItems: 'center',
    marginBottom: 20,
  },
  videoPlaceholder: {
    width: '100%',
    height: 200,
    backgroundColor: '#2a2a2a',
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  realVideo: {
    width: '100%',
    height: 220,
    backgroundColor: '#000000',
    borderRadius: 12,
    overflow: 'hidden',
  },
  openMeetingBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 14,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderRadius: 20,
  },
  openMeetingText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  demoTag: {
    position: 'absolute',
    top: 10,
    left: 10,
    backgroundColor: 'rgba(239,68,68,0.9)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  demoTagText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '900',
    letterSpacing: 1,
  },
  videoLabel: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 12,
  },
  videoRole: {
    color: '#ffffff',
    fontSize: 14,
    opacity: 0.7,
    marginTop: 4,
  },
  speakingIndicator: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    backgroundColor: '#10b981',
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  slidesContainer: {
    backgroundColor: '#2a2a2a',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  slidesTitle: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  slideCard: {
    backgroundColor: '#3a3a3a',
    borderRadius: 8,
    padding: 16,
    marginRight: 12,
    width: 140,
  },
  activeSlide: {
    backgroundColor: '#0e4e96',
  },
  slideNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  slideNumberText: {
    color: '#000',
    fontSize: 14,
    fontWeight: 'bold',
  },
  slideTitle: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  slideContent: {
    color: '#ffffff',
    fontSize: 12,
    opacity: 0.8,
  },
  reactionsContainer: {
    position: 'absolute',
    top: 10,
    right: 10,
    alignItems: 'flex-end',
  },
  reactionBubble: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginBottom: 8,
  },
  reactionText: {
    fontSize: 20,
  },
  pollBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#0e4e96',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
  },
  pollBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  pollBannerText: {
    color: '#ffffff',
    fontSize: 14,
    marginLeft: 8,
    flex: 1,
  },
  controlsSection: {
    backgroundColor: '#2a2a2a',
    paddingVertical: 12,
  },
  controlsScroll: {
    paddingHorizontal: 12,
  },
  controlButton: {
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    marginHorizontal: 4,
    minWidth: 80,
    backgroundColor: '#3a3a3a',
  },
  controlButtonActive: {
    backgroundColor: '#0e4e96',
  },
  controlText: {
    color: '#ffffff',
    fontSize: 12,
    marginTop: 4,
  },
  tabsContainer: {
    backgroundColor: '#ffffff',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 8,
    marginHorizontal: 8,
    borderRadius: 20,
    backgroundColor: '#f3f4f6',
  },
  activeTab: {
    backgroundColor: '#dbeafe',
    borderWidth: 1,
    borderColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    color: '#6b7280',
    marginLeft: 6,
    fontWeight: '500',
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: 'bold',
  },
  chatSection: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  chatHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  chatTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  chatActions: {
    flexDirection: 'row',
    gap: 12,
  },
  chatMessages: {
    flex: 1,
    padding: 16,
  },
  messageContainer: {
    backgroundColor: '#f8fafc',
    padding: 12,
    borderRadius: 12,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#d1d5db',
  },
  instructorMessage: {
    backgroundColor: '#dbeafe',
    borderLeftColor: '#0e4e96',
  },
  messageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  messageAvatar: {
    fontSize: 20,
    marginRight: 8,
  },
  messageInfo: {
    flex: 1,
  },
  messageUser: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4b5563',
  },
  instructorName: {
    color: '#0e4e96',
  },
  messageTime: {
    fontSize: 12,
    color: '#9ca3af',
  },
  messageText: {
    fontSize: 15,
    color: '#1f2937',
    lineHeight: 20,
  },
  chatInputContainer: {
    padding: 16,
    backgroundColor: '#f8fafc',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  reactionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 12,
  },
  reactionButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  reactionEmoji: {
    fontSize: 20,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  chatInput: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    fontSize: 16,
    maxHeight: 100,
  },
  sendButton: {
    backgroundColor: '#0e4e96',
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  sendButtonDisabled: {
    backgroundColor: '#e5e7eb',
  },
  questionsSection: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  questionsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  questionsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  questionsList: {
    flex: 1,
    padding: 16,
  },
  questionCard: {
    backgroundColor: '#f8fafc',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  questionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  questionAvatar: {
    fontSize: 20,
    marginRight: 12,
  },
  questionInfo: {
    flex: 1,
  },
  questionUser: {
    fontSize: 15,
    fontWeight: '600',
    color: '#4b5563',
  },
  questionTime: {
    fontSize: 12,
    color: '#9ca3af',
  },
  questionText: {
    fontSize: 16,
    color: '#1f2937',
    marginBottom: 16,
    lineHeight: 22,
  },
  questionActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  likeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#ffffff',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  likeCount: {
    fontSize: 14,
    color: '#6b7280',
    marginLeft: 6,
  },
  answerStatus: {
    backgroundColor: '#fef3c7',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  answeredStatus: {
    backgroundColor: '#d1fae5',
  },
  answerStatusText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#92400e',
  },
  pollsSection: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  pollsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  pollsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  activePollCard: {
    backgroundColor: '#f0f9ff',
    margin: 16,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#dbeafe',
  },
  pollHeader: {
    marginBottom: 20,
  },
  pollQuestion: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 8,
  },
  pollTime: {
    fontSize: 14,
    color: '#6b7280',
  },
  pollOption: {
    marginBottom: 16,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  optionCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#0e4e96',
    marginRight: 12,
  },
  optionText: {
    fontSize: 16,
    color: '#1f2937',
    flex: 1,
  },
  pollResult: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pollBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    overflow: 'hidden',
    marginRight: 12,
  },
  pollBar: {
    height: '100%',
    backgroundColor: '#0e4e96',
    borderRadius: 4,
  },
  optionPercentage: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0e4e96',
  },
  totalVotes: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    marginTop: 16,
  },
  leaveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ef4444',
    paddingVertical: 16,
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  leaveButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 'bold',
    marginLeft: 12,
  },
  // Whiteboard Styles
  whiteboardContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  whiteboardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#f8fafc',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  whiteboardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  whiteboardActions: {
    flexDirection: 'row',
    gap: 16,
  },
  whiteboardToolbar: {
    backgroundColor: '#f8fafc',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  whiteboardToolButton: {
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    marginHorizontal: 8,
    minWidth: 80,
  },
  activeWhiteboardTool: {
    backgroundColor: '#0e4e96',
  },
  whiteboardToolText: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  colorPicker: {
    padding: 16,
    backgroundColor: '#f8fafc',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  colorButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginHorizontal: 8,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  activeColorButton: {
    borderColor: '#000000',
    transform: [{ scale: 1.2 }],
  },
  strokePicker: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 16,
    gap: 16,
  },
  strokeButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#e5e7eb',
  },
  activeStrokeButton: {
    backgroundColor: '#d1d5db',
  },
  strokePreview: {
    borderRadius: 10,
  },
  whiteboardArea: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  whiteboardSvg: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  participantsModal: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  participantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  participantInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  participantAvatar: {
    fontSize: 24,
    marginRight: 12,
  },
  participantName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
  },
  participantRole: {
    fontSize: 14,
    color: '#6b7280',
  },
  speakingBadge: {
    backgroundColor: '#10b981',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  modalFooter: {
    padding: 20,
    backgroundColor: '#f8fafc',
  },
  muteAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ef4444',
    paddingVertical: 12,
    borderRadius: 8,
  },
  muteAllText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  // Settings Modal
  settingsOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  settingsModal: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    width: '90%',
    maxHeight: '80%',
    overflow: 'hidden',
  },
  settingsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  settingsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  settingsContent: {
    padding: 20,
  },
  settingsSection: {
    marginBottom: 24,
  },
  settingsSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 12,
  },
  settingsOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    marginBottom: 8,
  },
  selectedOption: {
    backgroundColor: '#dbeafe',
    borderWidth: 1,
    borderColor: '#0e4e96',
  },
  optionText: {
    fontSize: 16,
    color: '#1f2937',
  },
  connectionInfo: {
    backgroundColor: '#f9fafb',
    padding: 16,
    borderRadius: 8,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  infoLabel: {
    fontSize: 16,
    color: '#6b7280',
  },
  infoValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
  },
  connectionStatus: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  goodStatus: {
    backgroundColor: '#d1fae5',
  },
  excellentStatus: {
    backgroundColor: '#dbeafe',
  },
  fairStatus: {
    backgroundColor: '#fef3c7',
  },
  poorStatus: {
    backgroundColor: '#fee2e2',
  },
  settingsFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 12,
  },
  testAudioButton: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  testAudioText: {
    fontSize: 16,
    color: '#4b5563',
    fontWeight: '600',
  },
  testVideoButton: {
    flex: 1,
    backgroundColor: '#0e4e96',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  testVideoText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
});

export default LiveVideoSessionScreen;